import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';

import { Button, IconButton, Icons, Modal, Select, SpinnerOverlay } from '@components/common';
import FlatTreeXML from '@components/common/FlatXMLTree';

import {
	useMutateExportNetworkElementConfiguration,
} from '@hooks/networkElementConfigurations/useMutateExportNetworkElementConfiguration';
import useMutateNetworkElementCandidate from '@hooks/networkElementConfigurations/useMutateNetworkElementCandidate';
import useMutateNetworkElementConfigurationByDate
	from '@hooks/networkElementConfigurations/useMutateNetworkElementConfigurationByDate';
import useMutateNetworkElementConfigurationTimestamps
	from '@hooks/networkElementConfigurations/useMutateNetworkElementConfigurationTimestamps';
import useMutateNetworkElementValidationRules
	from '@hooks/networkElementConfigurations/useMutateNetworkElementValidationRules';
import useQueryNetworkElementConfigurationDates
	from '@hooks/networkElementConfigurations/useQueryNetworkElementConfigurationDates';
import useQueryNetworkElementConfigurationLatest
	from '@hooks/networkElementConfigurations/useQueryNetworkElementConfigurationLatest';
import useQueryNetworkElementValidationRulesFolders
	from '@hooks/networkElementConfigurations/useQueryNetworkElementValidationRulesFolders';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { formatDate } from '@utils/date';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	IIsRuleAvailable,
	IValidationRuleDTO,
} from '@interfaces/networkElementConfigurations/dto/networkElementConfigurationsDTO';
import {
	INetworkElementConfiguration,
	INetworkElementConfigurationView,
} from '@interfaces/networkElementConfigurations/networkElementConfigurations';
import { IFlatXMLTreeError, TMapTree } from '@components/common/FlatXMLTree/interface';

import { ESnackbarTypes } from '@enums/common';

import { responseMessages } from '@constants/responseMessages';
import { VALIDATION_FOLDER_AVAILABILITY_ERROR, VALIDATION_FOLDER_EMPTY_ERROR } from '../constants';

import {
	filterChangedNodes,
	formatAvailableTimestamps,
	getXMLString,
	parseRawConfig,
	pickLeftSideDates,
	prepareDataForSendingToCandidate,
} from '../utils';

import NetworkConfigurationsNotes from '../NetworkConfigurationsNotes';
import ViewNetworkConfigurationControl from './ViewNetworkConfigurationControl';

import styles from './NetworkConfigurationModal.module.scss';

interface INetworkConfigurationModalProps {
	config: INetworkElementConfiguration | null;
	isEditMode?: boolean;
	onClose: () => void;
	setIsDirtyForm: (isDirty: boolean) => void;
	closeViewModal: () => void;
}

const NetworkConfigurationModal = ({
	config,
	isEditMode = false,
	onClose,
	setIsDirtyForm,
	closeViewModal,
}: INetworkConfigurationModalProps) => {
	const dispatch = useAppDispatch();

	const {
		middlewareId = '',
		networkElementId = '',
	} = config || {};

	const today = useMemo(() => new Date(), []);

	const [selectedDate, setSelectedDate] = useState<Date | undefined>();
	const [selectedTimestamp, setSelectedTimestamp] = useState<TSelectOption<number>>(null);
	const [selectedConfiguration, setSelectedConfiguration] = useState<INetworkElementConfigurationView | undefined>();
	const [noteText, setNoteText] = useState<string>('');
	const [isOpenNoteModal, setIsOpenNoteModal] = useState(false);
	const [isDirtyNotesForm, setIsDirtyNotesForm] = useState(false);

	const [treeData, setTreeData] = useState<TMapTree>(new Map());
	const [changedIds, setChangedIds] = useState<string[]>([]);
	const [isCandidateTab, setIsCandidateTab] = useState<boolean>(false);
	const [candidateData, setCandidateData] = useState<TMapTree>(new Map());
	const [configDateText, setConfigDateText] = useState<string>('');

	const [isWorkerLoading, setIsWorkerLoading] = useState(false);
	const [isConfigTimestampMismatch, setIsConfigTimestampMismatch] = useState(true);

	const [selectedValidationRule, setSelectedValidationRule] = useState<TSelectOption<string, IIsRuleAvailable>>(null);

	const [
		candidateValidationErrors,
		setCandidateValidationErrors,
	] = useState<Map<string, IFlatXMLTreeError> | null>(null);

	const {
		validationRulesFolders,
		isLoadingValidationRulesFolders,
	} = useQueryNetworkElementValidationRulesFolders();

	const {
		getValidationRules,
		isLoadingValidationRules,
	} = useMutateNetworkElementValidationRules();

	const { exportNetworkElementConfiguration } = useMutateExportNetworkElementConfiguration();

	const {
		networkElementConfigurationDates,
		isLoadingNetworkElementConfigurationDates,
	} = useQueryNetworkElementConfigurationDates({
		middlewareId,
		networkElementId,
	});

	const {
		networkElementConfigurationTimestamps,
		isLoadingNetworkElementConfigurationTimestamps,
		getNetworkElementConfigurationTimestamps,
	} = useMutateNetworkElementConfigurationTimestamps();

	const openNoteModal = useCallback(() => setIsOpenNoteModal((prevState) => !prevState), []);

	const closeNoteModal = useCallback(() => {
		setIsOpenNoteModal(false);
		setIsDirtyNotesForm(false);
	}, []);

	const onCandidateSuccess = useCallback(() => {
		dispatch(setSnackbar({
			type: ESnackbarTypes.success,
			message: responseMessages.candidateSuccess,
		}));

		closeViewModal();
	}, [
		closeViewModal,
		dispatch,
	]);

	const {
		candidateNetworkElementConfiguration,
		isLoadingNetworkElementCandidate,
	} = useMutateNetworkElementCandidate(onCandidateSuccess);

	const setConfigByDate = (data: INetworkElementConfigurationView) => {
		setSelectedConfiguration(data);
		setPreparedTreeData(data);
	};

	const {
		isLoadingNetworkElementConfiguration,
		getNetworkElementConfigurationByDate,
	} = useMutateNetworkElementConfigurationByDate(setConfigByDate);

	const handleViewConfiguration = useCallback(() => {
		if (selectedTimestamp && selectedTimestamp?.value) {
			getNetworkElementConfigurationByDate({
				middlewareId,
				networkElementId,
				configDate: selectedTimestamp.value,
			});
		}
	}, [
		selectedTimestamp,
		middlewareId,
		networkElementId,
		getNetworkElementConfigurationByDate,
	]);

	const {
		latestNetworkElementConfiguration,
		isLoadingNetworkElementConfigurationLatest,
	} = useQueryNetworkElementConfigurationLatest({
		middlewareId,
		networkElementId,
	});

	const getConfigurationDateText = useCallback(({
		latestStoredAtText,
		currentStoredAtText,
		isConfigTimestampMismatch,
	}: {
		latestStoredAtText?: string;
		currentStoredAtText?: string;
		isConfigTimestampMismatch?: boolean;
	}) => {
		const isLatestSelected = currentStoredAtText === latestStoredAtText;

		return isConfigTimestampMismatch
			? currentStoredAtText || ''
			: `${currentStoredAtText} ${isLatestSelected ? '(last actual)' : ''}`;
	}, []);

	const isLoading = isLoadingNetworkElementConfiguration
		|| isLoadingNetworkElementConfigurationDates
		|| isLoadingNetworkElementConfigurationTimestamps
		|| isLoadingNetworkElementConfigurationLatest
		|| isLoadingNetworkElementCandidate
		|| isWorkerLoading
		|| isLoadingValidationRules;

	const isTimestampEqualsStoredAt = selectedConfiguration?.storedAt === selectedTimestamp?.value;

	const isViewButtonDisabled = !selectedTimestamp ||
		!!treeData.size ||
		isLoading;

	const isRefreshDisabled = selectedConfiguration?.storedAtText === latestNetworkElementConfiguration?.storedAtText
		&& !!treeData.size;

	const getValidationUpdateString = useCallback(async (id: string): Promise<IValidationRuleDTO | undefined> => {
		if (!id || !selectedValidationRule?.value) return;

		const filteredData = filterChangedNodes({
			data: treeData,
			changedIds: [id],
			isWithoutSiblings: true,
			isWithAlias: false,
		});
		const xmlString = getXMLString(filteredData);

		return {
			updateString: xmlString,
			validationRulesFolderId: selectedValidationRule.value,
		};
	}, [
		selectedValidationRule?.value,
		treeData,
	]);

	const toggleIsCandidateTab = useCallback(() => {
		setIsCandidateTab((prev) => !prev);
	}, []);

	const setPreparedTreeData = useCallback((selectedConfiguration: INetworkElementConfigurationView) => {
		const worker = new Worker(new URL('../parseWorker.js', import.meta.url), {
			type: 'module',
		});

		parseRawConfig(
			selectedConfiguration?.rawData,
			worker,
			setIsWorkerLoading,
		).then((data) => {
			setTreeData(data);

			setIsWorkerLoading(false);
		}).finally(() => {
			worker.terminate();
		});
	}, []);

	const handleDateUpdate = useCallback(async (date: number | null) => {
		if (!date) return;

		const formattedDate = new Date(date);
		const pickedLeftDate = pickLeftSideDates(networkElementConfigurationDates, formattedDate);
		const isMismatch = formatDate(formattedDate!) !== formatDate(pickedLeftDate);

		setIsConfigTimestampMismatch(isMismatch);
		setSelectedDate(formattedDate);

		const data = middlewareId && networkElementId && await getNetworkElementConfigurationTimestamps({
			middlewareId,
			networkElementId,
			configDate: formatDate(pickedLeftDate),
		});

		const formattedData = formatAvailableTimestamps(data);
		const defaultTimestamp = formattedData[formattedData.length - 1];

		if (formattedData.length) {
			setSelectedTimestamp(defaultTimestamp);
		}
	}, [
		networkElementConfigurationDates,
		middlewareId,
		networkElementId,
		getNetworkElementConfigurationTimestamps,
	]);

	const toggleAndSetCandidateData = useCallback(() => {
		toggleIsCandidateTab();
		setCandidateData(filterChangedNodes({ data: treeData, changedIds }));
		setCandidateValidationErrors(null);
	}, [toggleIsCandidateTab, treeData, changedIds, setCandidateData]);

	const handleSetNoteText = useCallback((text: string) => {
		setNoteText(text);
		setIsDirtyForm(false);
		closeNoteModal();
	}, [setIsDirtyForm, closeNoteModal]);

	const handleCandidate = useCallback(async () => {
		if (!selectedConfiguration || !selectedValidationRule?.value || !networkElementId) {
			return;
		}

		const updatedDataStrings = await prepareDataForSendingToCandidate(
			candidateData,
			changedIds,
			true,
		);

		await candidateNetworkElementConfiguration({
			configId: selectedConfiguration!.id,
			networkElementId,
			updateString: updatedDataStrings,
			validationRulesFolderId: selectedValidationRule.value,
			note: noteText,
		});
	}, [
		selectedConfiguration,
		selectedValidationRule?.value,
		candidateData,
		changedIds,
		noteText,
		candidateNetworkElementConfiguration,
		networkElementId,
	]);

	const onDateChange = useCallback(async (date: number | null) => {
		await handleDateUpdate(date);

		setTreeData(new Map());
	}, [
		handleDateUpdate,
		setTreeData,
	]);

	const onTimeStampsChange = useCallback((timestamp: TSelectOption<number>) => {
		if (!timestamp) return;

		setSelectedTimestamp(timestamp);
	}, []);

	const handleRefreshToLatest = useCallback(() => {
		if (latestNetworkElementConfiguration) {
			setSelectedConfiguration(latestNetworkElementConfiguration);
			handleDateUpdate(latestNetworkElementConfiguration?.storedAt);

			const worker = new Worker(new URL('../parseWorker.js', import.meta.url), {
				type: 'module',
			});

			parseRawConfig(
				latestNetworkElementConfiguration.rawData,
				worker,
				setIsWorkerLoading,
			).then((data) => {
				setTreeData(data);
				setIsWorkerLoading(false);
			}).finally(() => {
				worker.terminate();
			});
		}
	}, [
		latestNetworkElementConfiguration,
		setSelectedConfiguration,
		handleDateUpdate,
	]);

	const handleExport = useCallback(() => {
		if (selectedConfiguration) {
			exportNetworkElementConfiguration(selectedConfiguration);
		}
	}, [selectedConfiguration, exportNetworkElementConfiguration]);

	useEffect(() => {
		if (!networkElementConfigurationDates?.length) return;

		const latestData = networkElementConfigurationDates[networkElementConfigurationDates.length - 1];

		getNetworkElementConfigurationTimestamps({
			middlewareId,
			networkElementId,
			configDate: formatDate(latestData),
		}).then((data) => {
			const formattedData = formatAvailableTimestamps(data);
			const defaultTimestamp = formattedData[formattedData.length - 1];

			if (formattedData.length) {
				setSelectedTimestamp(defaultTimestamp);
			}
		});

		setSelectedDate(today);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [networkElementConfigurationDates?.length, today]);

	useEffect(() => {
		if (!networkElementConfigurationDates?.length || !latestNetworkElementConfiguration) return;

		const latestConfigDate = latestNetworkElementConfiguration.storedAtText.split(' ')[0];

		setSelectedConfiguration(latestNetworkElementConfiguration);
		setIsConfigTimestampMismatch(latestConfigDate !== formatDate(today));
	}, [
		today,
		latestNetworkElementConfiguration,
		networkElementConfigurationDates?.length,
	]);

	useEffect(() => {
		if (selectedConfiguration?.rawData) {
			setPreparedTreeData(selectedConfiguration);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedConfiguration?.rawData]);

	useEffect(() => {
		if (isEditMode) {
			setIsDirtyForm(!!changedIds.length);
		}
	}, [changedIds, isEditMode, setIsDirtyForm]);

	useEffect(() => {
		const timestamp = selectedTimestamp?.value;
		const hours = selectedTimestamp?.label;

		if (timestamp && hours) {
			const currentStoredAtText = `${formatDate(timestamp)} ${hours}`;

			const configurationDateText = getConfigurationDateText({
				currentStoredAtText,
				latestStoredAtText: latestNetworkElementConfiguration?.storedAtText,
				isConfigTimestampMismatch: currentStoredAtText !== latestNetworkElementConfiguration?.storedAtText,
			});

			setConfigDateText(configurationDateText);
		}
	}, [
		selectedTimestamp,
		latestNetworkElementConfiguration?.storedAtText,
		isConfigTimestampMismatch,
		getConfigurationDateText,
	]);

	const isTreeReadOnly = !isEditMode
		|| isCandidateTab
		|| !selectedValidationRule
		|| !selectedValidationRule?.isRulesAvailable;

	const selectError = !selectedValidationRule?.id
		? VALIDATION_FOLDER_EMPTY_ERROR
		: !selectedValidationRule?.isRulesAvailable
			? VALIDATION_FOLDER_AVAILABILITY_ERROR
			: undefined;

	const treeWrapperClasses = cn(styles.treeWrapper, {
		[styles.treeViewHeight]: !isEditMode,
		[styles.treeEditHeight]: isEditMode,
	});

	return (
		<div className={styles.wrapper}>
			{isLoading && <SpinnerOverlay />}
			{isOpenNoteModal && (
				<Modal
					isConfirmDialogNeededOnClose
					isDirtyForm={isDirtyNotesForm}
					isOpen={isOpenNoteModal}
					onClose={closeNoteModal}
					title='Add comment to changes'
				>
					<NetworkConfigurationsNotes
						config={null}
						defaultNoteText={noteText}
						isEditMode={isEditMode}
						onClose={closeNoteModal}
						onSubmit={handleSetNoteText}
						setIsDirtyForm={setIsDirtyNotesForm}
					/>
				</Modal>
			)}
			<div className={styles.header}>
				<div className={styles.control}>
					{isEditMode
						? (
							<Select
								errorMessage={selectError}
								handleChange={setSelectedValidationRule}
								isLoading={isLoadingValidationRulesFolders}
								isMulti={false}
								isWithoutUseForms
								label='Set YANG Model Collection'
								name='model'
								options={validationRulesFolders}
								placeholder='Choose YANG model collection'
								value={selectedValidationRule}
							/>
						)
						: (
							<ViewNetworkConfigurationControl
								isConfigTimestampMismatch={isConfigTimestampMismatch}
								isLoadingNetworkElementConfigurationDates={isLoadingNetworkElementConfigurationDates}
								networkElementConfigurationDates={networkElementConfigurationDates}
								networkElementConfigurationTimestamps={networkElementConfigurationTimestamps}
								onDateChange={onDateChange}
								onTimeStampsChange={onTimeStampsChange}
								selectedDate={selectedDate}
								selectedTimestamp={selectedTimestamp}
							/>
						)
					}
				</div>
				{selectedConfiguration && (
					<div className={styles.configurationDateWrapper}>
						<span>{isEditMode ? 'Last actual:' : 'Configuration used:'}</span>
						<span className={styles.configurationDate}>{configDateText}</span>
					</div>
				)}
			</div>
			{!isEditMode && (
				<div className={styles.viewButtonWrapper}>
					<Button
						disabled={isTimestampEqualsStoredAt && isViewButtonDisabled}
						large
						onClick={handleViewConfiguration}
						text='View configuration'
					/>
				</div>
			)}
			<div className={treeWrapperClasses}>
				<FlatTreeXML
					data={!isCandidateTab ? treeData : candidateData}
					errors={candidateValidationErrors || undefined}
					getValidationRules={getValidationRules}
					getValidationUpdateString={getValidationUpdateString}
					isReadOnly={isTreeReadOnly}
					setChangedNodeIds={setChangedIds}
					setData={!isCandidateTab ? setTreeData : setCandidateData}
				/>
			</div>
			<div className={styles.footer}>
				<div className={styles.errorsCount}>
					{candidateValidationErrors?.size && (
						<span>Errors: {candidateValidationErrors?.size}</span>
					)}
				</div>
				<div className={styles.buttonsWrapper}>
					<div className={styles.control}>
						{isCandidateTab && (
							<IconButton
								icon={Icons.description}
								onClick={openNoteModal}
								tooltipLabel='Add comment to changes'
							/>
						)}
						{!isEditMode && (
							<>
								<IconButton
									icon={Icons.refresh}
									isDisabled={isRefreshDisabled}
									onClick={handleRefreshToLatest}
									tooltipLabel='Reset to last actual'
								/>
								<IconButton
									icon={Icons.export}
									onClick={handleExport}
									tooltipLabel='Export config'
								/>
							</>
						)}
					</div>
					<div className={styles.control}>
						<Button
							large
							onClick={onClose}
							text='Close'
						/>
						{isEditMode && (
							<>
								{isCandidateTab ? (
									<>
										<Button
											large
											onClick={toggleIsCandidateTab}
											text='Back to edit'
										/>
										<Button
											disabled={!selectedConfiguration || !selectedValidationRule?.value}
											large
											onClick={handleCandidate}
											text='Candidate'
										/>
									</>
								) : (
									<Button
										disabled={!changedIds.length}
										large
										onClick={toggleAndSetCandidateData}
										text='Next'
									/>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default NetworkConfigurationModal;
